import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import ReactCountryFlag from 'react-country-flag'
import {
  getProcessedCountryData,
  getCountryByDialCode,
} from '../../utils/countryData'

/**
 * Custom Phone Input Component for editing
 * Provides country selector with phone number input
 */
const PhoneInput = ({
  value,
  onChange,
  isRequired = false,
  disabled = false,
  placeholder = 'Enter phone number',
  hasError = false,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [showCountrySelect, setShowCountrySelect] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const dropdownRef = useRef(null)

  // Get processed country data with flags and translated names
  const countryData = useMemo(() => {
    return getProcessedCountryData()
  }, [])

  // Sort countries alphabetically
  const sortedCountries = useMemo(() => {
    return countryData.sort((a, b) => a.name.localeCompare(b.name))
  }, [countryData])

  // Filter countries based on search term
  const filteredCountries = useMemo(() => {
    if (!searchTerm) return sortedCountries

    const searchLower = searchTerm.toLowerCase()
    return sortedCountries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchLower) ||
        country.dialCode.includes(searchTerm) ||
        country.code.toLowerCase().includes(searchLower),
    )
  }, [sortedCountries, searchTerm])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountrySelect(false)
        setSearchTerm('')
      }
    }

    if (showCountrySelect) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCountrySelect])

  // Extract country code and phone number from saved value
  const extractCountryAndPhone = (savedValue) => {
    if (!savedValue || typeof savedValue !== 'string') {
      return { countryCode: 'us', phoneNumber: '' }
    }

    // New format: +{dialCode}|{countryCode} {phoneNumber}
    const newFormatMatch = savedValue.match(/^\+(\d+)\|([a-z]{2})\s+(.+)$/)
    if (newFormatMatch) {
      const countryCode = newFormatMatch[2]
      const phoneNumber = newFormatMatch[3]
      return { countryCode, phoneNumber }
    }

    // Legacy format: +{dialCode} {phoneNumber} (for backward compatibility)
    const legacyMatch = savedValue.match(/^\+(\d+)\s+(.+)$/)
    if (legacyMatch) {
      const dialCode = legacyMatch[1]
      const phoneNumber = legacyMatch[2]

      // Find country by dial code - prefer US for dial code 1
      if (dialCode === '1') {
        return { countryCode: 'us', phoneNumber }
      }

      const countryByDialCode = getCountryByDialCode(dialCode)
      if (countryByDialCode) {
        return { countryCode: countryByDialCode.code, phoneNumber }
      }
    }

    return { countryCode: 'us', phoneNumber: savedValue }
  }

  // Initialize selected country and phone number from saved value
  useEffect(() => {
    if (sortedCountries.length > 0 && !isInitialized) {
      const { countryCode, phoneNumber: extractedPhone } =
        extractCountryAndPhone(value)

      // Find and set the selected country object
      const countryObj =
        sortedCountries.find((c) => c.code === countryCode) ||
        sortedCountries.find((c) => c.code === 'us') ||
        sortedCountries[0]

      if (countryObj) {
        setSelectedCountry(countryObj)
      }

      // Set the phone number
      setPhoneNumber(extractedPhone)
      setIsInitialized(true)
    }
  }, [value, sortedCountries, isInitialized])

  const handlePhoneChange = (e) => {
    const newPhoneNumber = e.target.value
    setPhoneNumber(newPhoneNumber)

    if (selectedCountry) {
      // Only create full value if there's actually a phone number
      if (newPhoneNumber && newPhoneNumber.trim()) {
        const fullValue = `+${selectedCountry.dialCode}|${selectedCountry.code} ${newPhoneNumber}`
        onChange(fullValue)
      } else {
        // Return empty string when phone number is cleared
        onChange('')
      }
    }
  }

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption)
    setShowCountrySelect(false)
    setSearchTerm('')

    // Update phone number with new country code only if there's a phone number
    if (phoneNumber && phoneNumber.trim()) {
      const fullValue = `+${selectedOption.dialCode}|${selectedOption.code} ${phoneNumber}`
      onChange(fullValue)
    } else {
      // Return empty string when there's no phone number
      onChange('')
    }
  }

  const handleDropdownToggle = () => {
    if (!disabled) {
      setShowCountrySelect(!showCountrySelect)
      if (!showCountrySelect) {
        setSearchTerm('')
      }
    }
  }

  return (
    <div className='d-flex align-items-center'>
      {/* Country selector dropdown */}
      <div
        className='position-relative me-2'
        style={{ minWidth: '80px' }}
        ref={dropdownRef}>
        <button
          type='button'
          className={classNames(
            'form-control d-flex align-items-center justify-content-between',
            {
              'is-invalid': hasError,
            },
          )}
          onClick={handleDropdownToggle}
          disabled={disabled}
          style={{
            background: disabled ? '#f8f9fa' : '#fff',
            height: '42px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            padding: '8px 12px',
            borderRadius: '4px',
          }}>
          {selectedCountry ? (
            <>
              <ReactCountryFlag
                countryCode={(selectedCountry?.code || 'us').toUpperCase()}
                svg
                className='me-2'
                style={{
                  width: '1.25em',
                  height: '1.25em',
                  borderRadius: '30%',
                }}
                aria-label={(selectedCountry?.code || 'us').toUpperCase()}
              />
            </>
          ) : (
            <span style={{ color: '#666', fontSize: '14px' }}>Select</span>
          )}
          <svg width='16' height='16' viewBox='0 0 16 16' className='ms-2'>
            <polyline
              points='4,6 8,10 12,6'
              fill='none'
              stroke='#000'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>

        {/* Simple dropdown */}
        {showCountrySelect && (
          <div
            className='position-absolute bg-white border rounded'
            style={{
              top: '100%',
              left: 0,
              zIndex: 1000,
              maxHeight: '300px',
              overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #ddd',
              backgroundColor: '#fff',
              marginTop: '2px',
              minWidth: '280px',
              width: 'max-content',
            }}>
            {/* Search input */}
            <div
              className='p-2 border-bottom'
              style={{ backgroundColor: '#f8f9fa' }}>
              <input
                type='text'
                placeholder='Search countries...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='form-control form-control-sm'
                style={{
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  padding: '6px 10px',
                }}
                autoFocus
              />
            </div>

            {/* Country list */}
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className='d-flex align-items-center px-3 py-2 cursor-pointer'
                  onClick={() => handleCountryChange(country)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    minWidth: '280px',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa'
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent'
                  }}>
                  <ReactCountryFlag
                    countryCode={country.code.toUpperCase()}
                    svg
                    className='me-2'
                    style={{
                      width: '1.25em',
                      height: '1.25em',
                      borderRadius: '30%',
                    }}
                    aria-label={country.code.toUpperCase()}
                  />
                  <span
                    className='flex-grow-1 text-truncate'
                    style={{ fontSize: '14px' }}>
                    {country.name}
                  </span>
                  <span
                    className='ms-2'
                    style={{ fontSize: '14px', color: '#666' }}>
                    +{country.dialCode}
                  </span>
                </div>
              ))
            ) : (
              <div
                className='p-3 text-center text-muted'
                style={{ fontSize: '14px' }}>
                No countries found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Phone number input */}
      <input
        type='tel'
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        disabled={disabled}
        className={classNames(
          'form-control flex-grow-1 placeholder-color phone-number-input',
          {
            'is-invalid': hasError,
          },
        )}
        required={isRequired}
        autoComplete='off'
      />
    </div>
  )
}

export default PhoneInput
